import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, TrendingUp, Calendar, DollarSign } from "lucide-react";
import { toast } from "sonner";

const MarketingPerformancePanel = ({ user }) => {
  const navigate = useNavigate();
  const [performanceData, setPerformanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("daily"); // daily, weekly, monthly
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false); // default: only show current period (today/this week/this month)
  const itemsPerPage = 10;

  useEffect(() => {
    fetchPerformance();
  }, []);

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("period", period);

      const response = await axiosInstance.get("/reports/marketing-performance?" + params.toString());
      setPerformanceData(response.data);
    } catch (error) {
      console.error("Error fetching performance:", error);
      toast.error(error.response?.data?.detail || "Gagal memuat data performance");
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = async (value) => {
    setPeriod(value);
    setCurrentPage(1); // Reset to first page when changing period
    // Fetch data immediately when period changes
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("period", value);

      const response = await axiosInstance.get("/reports/marketing-performance?" + params.toString());
      setPerformanceData(response.data);
    } catch (error) {
      console.error("Error fetching performance:", error);
      toast.error(error.response?.data?.detail || "Gagal memuat data performance");
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals (recomputed in filtered section below based on display filter)

  // Compute the "current period" key for filtering (matches backend date keys).
  const getCurrentPeriodKey = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    if (period === "daily") return `${yyyy}-${mm}-${dd}`;
    if (period === "monthly") return `${yyyy}-${mm}`;
    // ISO week (matches MongoDB %G-W%V used in backend)
    const tmp = new Date(Date.UTC(yyyy, d.getMonth(), d.getDate()));
    const dayNum = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    const weekNum = Math.ceil((((tmp - yearStart) / 86400000) + 1) / 7);
    return `${tmp.getUTCFullYear()}-W${String(weekNum).padStart(2, "0")}`;
  };

  // Apply "current period only" filter for the displayed table
  const filteredData = showAll
    ? performanceData
    : performanceData.filter((row) => row.date === getCurrentPeriodKey());

  // Recalculate totals based on what's displayed
  const totalNDP = filteredData.reduce((sum, p) => sum + p.total_ndp, 0);
  const totalDepo = filteredData.reduce((sum, p) => sum + p.total_depo, 0);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = filteredData.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Format date display based on actual data format (not selected period)
  const formatDate = (dateStr) => {
    const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", 
                       "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
    
    // Detect format from the data itself
    if (dateStr.includes("-W")) {
      // Weekly format: 2025-W47 -> Calculate start and end dates of the week
      const parts = dateStr.split("-W");
      if (parts.length === 2 && parts[1]) {
        const year = parseInt(parts[0]);
        const week = parseInt(parts[1]);
        
        // Calculate the date of the Monday of the given week
        // Week 1 is the week with the first Thursday of the year
        const jan4 = new Date(year, 0, 4); // January 4th
        const jan4Day = jan4.getDay() || 7; // Convert Sunday (0) to 7
        const weekStart = new Date(jan4);
        weekStart.setDate(jan4.getDate() - jan4Day + 1); // Move to Monday of week 1
        weekStart.setDate(weekStart.getDate() + (week - 1) * 7); // Add weeks
        
        // Calculate week end (Sunday)
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        
        // Format dates
        const startDay = weekStart.getDate();
        const startMonth = monthNames[weekStart.getMonth()];
        const startYear = weekStart.getFullYear();
        
        const endDay = weekEnd.getDate();
        const endMonth = monthNames[weekEnd.getMonth()];
        const endYear = weekEnd.getFullYear();
        
        // If same month and year
        if (startMonth === endMonth && startYear === endYear) {
          return `${startDay} - ${endDay} ${startMonth} ${startYear}`;
        }
        // If same year but different month
        else if (startYear === endYear) {
          return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${startYear}`;
        }
        // If different year
        else {
          return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
        }
      }
    } else if (dateStr.match(/^\d{4}-\d{2}$/)) {
      // Monthly format: 2025-11 -> November 2025
      const [year, month] = dateStr.split("-");
      const monthIndex = parseInt(month) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${monthNames[monthIndex]} ${year}`;
      }
    } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Daily format: 2025-11-26 -> 26 November 2025
      const [year, month, day] = dateStr.split("-");
      const monthIndex = parseInt(month) - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        return `${day} ${monthNames[monthIndex]} ${year}`;
      }
    }
    
    // Fallback: return original string if format not recognized
    return dateStr;
  };

  const getPeriodLabel = () => {
    if (period === "daily") return "Harian";
    if (period === "weekly") return "Mingguan";
    return "Bulanan";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0f0f10] via-[#1a1a1b] to-[#0f0f10]" data-testid="marketing-performance-panel">
      <header className="bg-[#1a1a1b]/80 backdrop-blur-xl border-b border-red-900/30 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/dashboard")}
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white hover:bg-red-900/20"
            >
              <ArrowLeft className="mr-2" size={18} />
              Kembali
            </Button>
            <h1 className="text-2xl font-bold text-red-50">Performance Report Marketing</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card className="bg-gradient-to-br from-green-900/20 to-green-800/10 border-green-700/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">Total Member NDP (Disetujui)</CardTitle>
              <TrendingUp className="w-5 h-5 text-green-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-400">{totalNDP}</div>
              <p className="text-xs text-gray-400 mt-1">Member yang sudah disubmit supervisor</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-blue-900/20 to-blue-800/10 border-blue-700/30">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-gray-300">Total Deposit</CardTitle>
              <DollarSign className="w-5 h-5 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-400">Rp {totalDepo.toLocaleString('id-ID')}</div>
              <p className="text-xs text-gray-400 mt-1">Total nilai deposit dari member NDP</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30 mb-6">
          <CardHeader>
            <CardTitle className="text-red-50">Filter Periode</CardTitle>
            <CardDescription className="text-gray-400">Pilih periode untuk melihat performance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Button
                onClick={() => handlePeriodChange("daily")}
                className={`flex-1 ${
                  period === "daily"
                    ? "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                    : "bg-[#0f0f10] border border-red-900/30 text-gray-300 hover:bg-red-900/20 hover:text-white"
                }`}
              >
                <Calendar className="mr-2" size={18} />
                Harian
              </Button>
              <Button
                onClick={() => handlePeriodChange("weekly")}
                className={`flex-1 ${
                  period === "weekly"
                    ? "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                    : "bg-[#0f0f10] border border-red-900/30 text-gray-300 hover:bg-red-900/20 hover:text-white"
                }`}
              >
                <Calendar className="mr-2" size={18} />
                Mingguan
              </Button>
              <Button
                onClick={() => handlePeriodChange("monthly")}
                className={`flex-1 ${
                  period === "monthly"
                    ? "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                    : "bg-[#0f0f10] border border-red-900/30 text-gray-300 hover:bg-red-900/20 hover:text-white"
                }`}
              >
                <Calendar className="mr-2" size={18} />
                Bulanan
              </Button>
            </div>
            <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-gray-400">
                {showAll
                  ? `Menampilkan semua periode (${performanceData.length} baris)`
                  : `Menampilkan ${period === "daily" ? "hari ini" : period === "weekly" ? "minggu ini" : "bulan ini"} saja`}
              </p>
              <Button
                onClick={() => { setShowAll(!showAll); setCurrentPage(1); }}
                variant="outline"
                size="sm"
                className="border-red-900/30 text-white hover:bg-red-900/20"
                data-testid="marketing-perf-toggle-showall"
              >
                {showAll ? "Tampilkan Periode Ini Saja" : "Lihat Semua Periode"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Performance Table */}
        <Card className="bg-[#1a1a1b]/80 backdrop-blur-xl border-red-900/30">
          <CardHeader>
            <CardTitle className="text-red-50">Rincian Performance {getPeriodLabel()}</CardTitle>
            <CardDescription className="text-gray-400">
              Menampilkan jumlah member NDP yang sudah disetujui supervisor berdasarkan tanggal member dibuat
              {!showAll && period === "daily" && " (hari ini)"}
              {!showAll && period === "weekly" && " (minggu ini)"}
              {!showAll && period === "monthly" && " (bulan ini)"}
              {showAll && period === "daily" && " (31 hari terakhir)"}
              {showAll && period === "weekly" && " (52 minggu terakhir)"}
              {showAll && period === "monthly" && " (12 bulan terakhir)"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-red-600 border-t-transparent"></div>
              </div>
            ) : filteredData.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400 text-lg">Tidak ada data untuk periode ini</p>
                <p className="text-gray-500 text-sm mt-2">
                  {!showAll ? "Klik \"Lihat Semua Periode\" untuk melihat data periode lain" : "Coba ubah filter periode"}
                </p>
              </div>
            ) : (
              <>
                <div className="rounded-lg border border-red-900/30 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-[#0f0f10] border-red-900/30 hover:bg-[#0f0f10]">
                        <TableHead className="text-red-400 font-semibold">Periode</TableHead>
                        <TableHead className="text-red-400 font-semibold text-center">Jumlah NDP</TableHead>
                        <TableHead className="text-red-400 font-semibold text-right">Total Deposit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentData.map((data) => (
                        <TableRow key={data.date} className="border-red-900/20 hover:bg-[#0f0f10]/50">
                          <TableCell className="font-medium text-white">
                            {formatDate(data.date)}
                          </TableCell>
                          <TableCell className="text-green-400 text-center font-semibold text-lg">
                            {data.total_ndp}
                          </TableCell>
                          <TableCell className="text-blue-400 text-right font-semibold">
                            Rp {data.total_depo.toLocaleString('id-ID')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-gray-400">
                      Menampilkan {startIndex + 1} - {Math.min(endIndex, performanceData.length)} dari {performanceData.length} data
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                        className="border-red-900/30 text-white hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Sebelumnya
                      </Button>
                      <div className="flex items-center gap-2 px-3 py-1 bg-[#0f0f10] border border-red-900/30 rounded text-white text-sm">
                        <span>Halaman {currentPage} dari {totalPages}</span>
                      </div>
                      <Button
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        size="sm"
                        className="border-red-900/30 text-white hover:bg-red-900/20 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Selanjutnya
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default MarketingPerformancePanel;
